/**
 * Utility functions for handling image URLs
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';
let SERVER_BASE_URL = API_BASE_URL.replace('/api', '');

// Prefer current origin in browsers to avoid mixed content
let BASE_URL = SERVER_BASE_URL;
if (typeof window !== 'undefined') {
  const origin = window.location.origin;
  const isHttps = origin.startsWith('https://');
  const isLocal = SERVER_BASE_URL.includes('localhost');
  const isAbsolute = SERVER_BASE_URL.startsWith('http://') || SERVER_BASE_URL.startsWith('https://');
  if (!isAbsolute || isLocal || (isHttps && SERVER_BASE_URL.startsWith('http://'))) {
    BASE_URL = origin; // same-origin to avoid mixed content
  }
}


// Debug logs removed

/**
 * Convert a relative image path to a full URL
 * @param imagePath - Relative path like "/uploads/logos/filename.jpg"
 * @returns Full URL like "http://localhost:3001/uploads/logos/filename.jpg"
 * Always includes the full server URL for proper image loading
 */
export const getImageUrl = (imagePath: string | null | undefined): string | null => {
  if (!imagePath) return null;
  
  // Clean the image path
  const cleanPath = imagePath.trim();
  if (!cleanPath) return null;
  
  // If it's already a full URL, return as-is
  if (cleanPath.startsWith('http://') || cleanPath.startsWith('https://')) {
    try {
      const url = new URL(cleanPath);
      if (typeof window !== 'undefined') {
        const origin = window.location.origin;
        // If protocol/host differ, rewrite to same-origin path to avoid mixed content/CORS
        if (url.origin !== origin) {
          return `${origin}${url.pathname}`;
        }
      }
    } catch {
      // fall through to return as-is
    }
    return cleanPath;
  }
  
  // If it starts with /, it's a relative path from server root
  if (cleanPath.startsWith('/')) {
    const fullUrl = `${BASE_URL.replace(/\/$/, '')}${cleanPath}`;
    return fullUrl;
  }
  
  // Special case: if it's a logo filename missing the prefix
  if (cleanPath.startsWith('logo-') && !cleanPath.includes('/')) {
    const fullUrl = `${BASE_URL.replace(/\/$/, '')}/uploads/logos/${cleanPath}`;
    return fullUrl;
  }
  
  // Otherwise, assume it's a relative path and prepend the server base
  const fullUrl = `${BASE_URL.replace(/\/$/, '')}/${cleanPath}`;
  return fullUrl;
};

/**
 * Check if an image URL is accessible
 * @param imageUrl - Full image URL
 * @returns Promise<boolean> - true if image is accessible
 */
export const checkImageAccessibility = async (imageUrl: string): Promise<boolean> => {
  try {
    const response = await fetch(imageUrl, { method: 'HEAD' });
    return response.ok;
  } catch (error) {
    console.error('Image accessibility check failed:', error);
    return false;
  }
};

/**
 * Get a fallback image URL or return null
 * @param imagePath - The original image path
 * @param fallbackPath - Optional fallback image path
 * @returns Full URL or null
 */
export const getImageUrlWithFallback = (
  imagePath: string | null | undefined, 
  fallbackPath?: string
): string | null => {
  const primaryUrl = getImageUrl(imagePath);
  if (primaryUrl) return primaryUrl;
  
  return fallbackPath ? getImageUrl(fallbackPath) : null;
};
